import {
  SHAutocomplete,
  SHCheckbox,
  SHStack,
  SHTextFieldProps,
  SHTypography,
} from "@components/design-systems";
import { useInvestment } from "@hooks/useInvestment";
import { useManagedAccount } from "@hooks/useManagedAccount";
import {
  InvestmentQueryOptions,
  InvestmentSelectionDTO,
  SelectedInvestmentDTO,
} from "@models/managed-accounts/entities/step/setup";
import { isEmpty, isNil } from "lodash";
import React, {
  cloneElement,
  forwardRef,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { Virtuoso } from "react-virtuoso";
import { getSetupRowWarningMessage } from "@pages/platform-analysis/_id/steps/fee/calculator/components/portfolio-setup/utils";
import { BalanceAvailability } from "@models/platform-analysis/enums/fee/balanceAvailability";
import { AnalysisFeePortfolioWarningIcon } from "@pages/platform-analysis/_id/steps/fee/calculator/components/portfolio-setup/warning-icon";

export interface SelectInvestmentsFavouritedProps {
  selectedCodes: string[] | null;
  maxLength?: number;
  disabled?: boolean;
  textFieldProps?: Omit<SHTextFieldProps, "value">;
  initialData?: InvestmentSelectionDTO[];

  onAppend: (value: SelectedInvestmentDTO) => void;
  onRemove: (index: string) => void;
  onRemoveAll: () => void;
  onLoaded?: () => void;
  hasChange?: (value: boolean) => void;
  popperPlacementAlwaysOnTop?: boolean;
}

export const SelectInvestmentsFavourited = ({
  disabled,
  textFieldProps,
  selectedCodes = [],
  initialData,
  onRemove,
  onAppend,
  onRemoveAll,
  onLoaded,
  hasChange,
  popperPlacementAlwaysOnTop = true,
}: SelectInvestmentsFavouritedProps) => {
  const { managedAccountId } = useManagedAccount();
  const {
    investmentFavourited,
    setInvestmentFavourited,
    loadInvestmentFavourited,
    setOriginalInvestmentFavourited,
  } = useInvestment();

  const [inputValue, setInputValue] = useState("");

  const selectedInvestments = investmentFavourited.filter((option) =>
    selectedCodes?.includes(option.shCode),
  );

  const loadData = async (queryOptions: InvestmentQueryOptions) => {
    const validInitialData = !isNil(initialData);
    const responseData = validInitialData
      ? initialData
      : await loadInvestmentFavourited(queryOptions);
    if (validInitialData) {
      setOriginalInvestmentFavourited(responseData);
    }

    if (responseData) setInvestmentFavourited(responseData);
  };

  useEffect(() => {
    const queryOptions: InvestmentQueryOptions = {
      managedAccountId: managedAccountId ?? "new",
    };

    loadData(queryOptions).then(() => {
      onLoaded && onLoaded();
    });
    // eslint-disable-next-line
  }, [managedAccountId, initialData]);

  // eslint-disable-next-line
  const ListboxComponent = React.useCallback(
    forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
      (
        props: React.HTMLAttributes<HTMLElement>,
        ref: React.ForwardedRef<HTMLDivElement>,
      ) => {
        const { children, tabIndex, ...others } = props;
        const dataItems = children as ReactElement[];

        const itemHeight = 44;
        const maxItemDisplayed = 6;
        const currentItemCount = dataItems.length;

        const getCurrentHeight = () => {
          if (currentItemCount === 1) return currentItemCount * itemHeight;
          if (currentItemCount >= maxItemDisplayed) {
            return maxItemDisplayed * itemHeight + 10;
          }
          return currentItemCount * itemHeight + 10;
        };

        return (
          <div ref={ref}>
            <Virtuoso
              style={{ height: getCurrentHeight() }}
              data={dataItems}
              itemContent={(_, child) => {
                return cloneElement(child);
              }}
              {...others}
            />
          </div>
        );
      },
    ),
    [],
  );

  return (
    <SHStack spacing={"16px"} direction="column">
      <SHAutocomplete
        multiple
        disabled={disabled}
        disableCloseOnSelect
        disableListWrap
        isOptionEqualToValue={(option, value) =>
          option.shCode === value?.shCode
        }
        onClose={() => {
          setInputValue("");
        }}
        inputValue={inputValue}
        value={selectedInvestments ?? []}
        getOptionLabel={(option: InvestmentSelectionDTO) =>
          `${option.displayName} ${option.code}}` ?? ""
        }
        componentsProps={{
          popper: {
            placement: popperPlacementAlwaysOnTop ? "top" : undefined,
          },
        }}
        textFieldProps={{
          ...textFieldProps,
          InputLabelProps: { shrink: true },
          placeholder: selectedInvestments?.length
            ? undefined
            : textFieldProps?.placeholder,
          InputProps: {
            startAdornment: selectedInvestments?.length ? (
              <SHTypography
                disabled={disabled}
                variant="body1"
                sx={{ paddingLeft: "10px" }}
              >
                {`${selectedInvestments?.length} selected`}
              </SHTypography>
            ) : undefined,
          },
          onChange: (e) => {
            setInputValue(e.target.value);
          },
        }}
        onChange={(event: any, newValue, reason, details) => {
          if (
            reason === "removeOption" &&
            event?.type === "keydown" &&
            event?.key === "Backspace"
          ) {
            return;
          }
          if (hasChange) hasChange(true);
          if (isEmpty(newValue)) {
            onRemoveAll();
            return;
          }
          if (
            newValue.some((option) => option.shCode === details?.option.shCode)
          ) {
            onAppend({
              ...details?.option,
              weight: 0,
              value: 0,
              shCode: details?.option?.shCode ?? "",
            });
          } else onRemove(details?.option.shCode ?? "");
        }}
        options={investmentFavourited}
        renderOption={(props, option, { selected }) => {
          const {
            balanceAvailability,
            isUnavailable,
            shCode,
            code,
            displayName,
          } = option || {};
          const warning = getSetupRowWarningMessage({
            balanceAvailability:
              balanceAvailability || BalanceAvailability.Show,
          });

          return (
            <li
              {...props}
              onClick={(e) => {
                if (isUnavailable) return;
                props.onClick?.(e);
              }}
              key={shCode}
              style={{ padding: "1px 10px" }}
            >
              <SHStack alignItems={"center"} direction="row" spacing={1}>
                <SHCheckbox disabled={isUnavailable} checked={selected} />
                {code && (
                  <SHTypography
                    disabled={isUnavailable}
                    variant="body3"
                    fontWeight={400}
                    colorVariant="disabled"
                  >
                    {code}
                  </SHTypography>
                )}

                <SHTypography
                  disabled={isUnavailable}
                  variant="body3"
                  fontWeight={500}
                >
                  {displayName}
                </SHTypography>
                {warning && (
                  <AnalysisFeePortfolioWarningIcon
                    type={"warning"}
                    warningData={warning}
                  />
                )}
              </SHStack>
            </li>
          );
        }}
        ListboxComponent={ListboxComponent}
      />
    </SHStack>
  );
};
